import { useEffect } from "react";
import { SITE_URL, DEFAULT_OG_IMAGE } from "./seoConfig";

const TITLE_SUFFIX = "Hordaland Kunstsenter";

// ---------------------------------------------------------------------------
// Low-level DOM helpers
// ---------------------------------------------------------------------------

function getOrCreateMetaName(name) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  return el;
}

function getOrCreateMetaProperty(property) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  return el;
}

function getOrCreateCanonical() {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  return el;
}

// ---------------------------------------------------------------------------
// Core update function
// ---------------------------------------------------------------------------

/**
 * updatePageHead({ title, description, keywords, image, url })
 *
 * Sets <title>, description/keywords meta, OG tags, Twitter card, and
 * canonical link. All arguments are optional; missing values fall back
 * to site-level defaults.
 */
export function updatePageHead({ title, description, keywords, image, url } = {}) {
  const fullTitle = title
    ? `${title} | ${TITLE_SUFFIX}`
    : TITLE_SUFFIX;
  const resolvedDesc = description || "";
  const resolvedImage = image || DEFAULT_OG_IMAGE;
  const resolvedUrl = url || SITE_URL;

  // <title>
  document.title = fullTitle;

  // Standard meta
  getOrCreateMetaName("description").setAttribute("content", resolvedDesc);
  if (keywords) {
    getOrCreateMetaName("keywords").setAttribute("content", keywords);
  }

  // Open Graph
  getOrCreateMetaProperty("og:type").setAttribute("content", "website");
  getOrCreateMetaProperty("og:site_name").setAttribute("content", TITLE_SUFFIX);
  getOrCreateMetaProperty("og:title").setAttribute("content", fullTitle);
  getOrCreateMetaProperty("og:description").setAttribute("content", resolvedDesc);
  getOrCreateMetaProperty("og:image").setAttribute("content", resolvedImage);
  getOrCreateMetaProperty("og:url").setAttribute("content", resolvedUrl);

  // Twitter / Slack
  getOrCreateMetaName("twitter:card").setAttribute("content", "summary_large_image");
  getOrCreateMetaName("twitter:title").setAttribute("content", fullTitle);
  getOrCreateMetaName("twitter:description").setAttribute("content", resolvedDesc);
  getOrCreateMetaName("twitter:image").setAttribute("content", resolvedImage);

  // Canonical
  getOrCreateCanonical().setAttribute("href", resolvedUrl);
}

// ---------------------------------------------------------------------------
// React hook
// ---------------------------------------------------------------------------

/**
 * usePageHead({ title, description, keywords, image, path })
 *
 * Thin React hook wrapper. Call inside any functional component.
 * `path` should be the current pathname (e.g. useLocation().pathname).
 */
export function usePageHead({ title, description, keywords, image, path } = {}) {
  const url = path ? `${SITE_URL}${path}` : undefined;
  useEffect(() => {
    updatePageHead({ title, description, keywords, image, url });
  }, [title, description, keywords, image, url]);
}

// ---------------------------------------------------------------------------
// Legacy compat – keep setTitle in case it's imported elsewhere
// ---------------------------------------------------------------------------

export function setTitle(name) {
  document.title = name ? `${name} | ${TITLE_SUFFIX}` : TITLE_SUFFIX;
}
